/*
 * -----------------------------------------------------------------------
 *  <copyright file="org-admin.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql } from '@apollo/client';
import {
    OrganisationIndicationFragment,
    OrganisationProductFragment,
    OrganisationProgramFragment,
    SystemOrganisationFragment, SystemOrganisationKeyNameFragment
} from './fragments';

export const SAVE_ORGANISATION_INDICATION = gql`
    mutation saveOrganisationIndication($indication: OrganisationIndicationInput!, $reason: String) {
        saveOrganisationIndication(indication: $indication, reason: $reason) {
            ...OrganisationIndicationFields
        }
    }
    ${OrganisationIndicationFragment}
`;

export const SAVE_ORGANISATION_PRODUCT = gql`
    mutation saveOrganisationProduct($product: OrganisationProductInput!, $reason: String) {
        saveOrganisationProduct(product: $product, reason: $reason) {
            ...OrganisationProductFields
        }
    }
    ${OrganisationProductFragment}
`;

export const SAVE_ORGANISATION_PROGRAM = gql`
    mutation saveOrganisationProgram($program: OrganisationProgramInput!, $reason: String) {
        saveOrganisationProgram(program: $program, reason: $reason) {
            ...OrganisationProgramFields
        }
    }
    ${OrganisationProgramFragment}
`;

// ////////////////////////////////////////////////////////////////////////////////////
// System / Manage Organisations - Queries & Mutations
// ////////////////////////////////////////////////////////////////////////////////////

// Adds a new organisation to the system - returns null if success, otherwise error message
export const ADD_ORGANISATION = gql`
    mutation addOrganisation(
        $name: String!,
        $region: DataStorageRegion!,
        $urlPath: String!,
        $connectionString: String,
        $useTransactions: Boolean) 
    {
        addOrganisation(
            name: $name,
            region: $region,
            urlPath: $urlPath,
            connectionString: $connectionString,
            useTransactions: $useTransactions)
    }
`;

// Gets all of the "system" level organisation data (name, URL path, region config) for all OPRA organisations
export const GET_SYSTEM_ORGANISATIONS = gql`
    query systemOrganisations {
        systemOrganisations {
            ...SystemOrganisationFields
        }
    }
    ${SystemOrganisationFragment}
`;

export const GET_SYSTEM_ORGANISATIONS_KEY_NAME = gql`
    query systemOrganisationsKeyName {
        systemOrganisations {
            ...SystemOrganisationKeyNameFields
        }
    }
    ${SystemOrganisationKeyNameFragment}
`;

export const DELETE_ORGANISATION = gql`
    mutation deleteOrganisation($key: ObjectId!) {
        deleteOrganisation(key: $key)
    }
`;

// Saves the name and url path for an organisation
// Returns null if success, otherwise error message
export const SAVE_ORGANISATION_CONFIGURATION = gql`
    mutation saveOrganisationConfiguration($organisationKey: ObjectId!, $name: String!, $urlPath: String!) {
        saveOrganisationConfiguration(organisationKey: $organisationKey, name: $name, urlPath: $urlPath)
    }
`;

// Saves the organisation type and primary contact details
// Returns null if success, otherwise error message
export const SAVE_ORGANISATION_DETAILS = gql`
    mutation saveOrganisationDetails(
        $organisationKey: ObjectId!,
        $type: OrganisationType!,
        $primaryContact: OrganisationContactInput,
        $reason: String) 
    {
        saveOrganisationDetails(
            organisationKey: $organisationKey,
            type: $type,
            primaryContact: $primaryContact,
            reason: $reason)
    }
`;

// Sets the logo for an organisation (logo is a base-64 encoded string, reason can be empty)
// Returns null if success, otherwise error message
export const SAVE_ORGANISATION_LOGO = gql`
    mutation saveOrganisationLogo($organisationKey: ObjectId!, $logo: String, $reason: String) {
        saveOrganisationLogo(organisationKey: $organisationKey, logo: $logo, reason: $reason)
    }
`;

// Saves the security details for an organisation
// Returns null if success, otherwise error message
//
// OrganisationSecurityInput {
//      accountInactiveDays: Int
//      minLowercaseCount: Int
//      minNumberCount: Int
//      minPasswordLength: Int
//      minSpecialCharCount: Int
//      minUppercaseCount: Int
//      numAttemptsBeforeLockout: Int
//      twoFactorEnabled: Boolean
//      userIdleTimeoutMinutes: Int
// }
export const SAVE_ORGANISATION_SECURITY = gql`
    mutation saveOrganisationSecurity($organisationKey: ObjectId!, $security: OrganisationSecurityInput!, $reason: String) {
        saveOrganisationSecurity(organisationKey: $organisationKey, security: $security, reason: $reason)
    }
`;

// Saves the modules for an organisation
// CM = 1, RAM = 2, ISMP = 4
// To save the modules, add up all of the selected modules and post that as the "modules" argument
// Returns null if success, otherwise error message
export const SAVE_ORGANISATION_MODULES = gql`
    mutation saveOrganisationModules($organisationKey: ObjectId!, $modules: ConvertableInt!, $reason: String) {
        saveOrganisationModules(organisationKey: $organisationKey, modules: $modules, reason: $reason)
    }    
`;

export const HAS_INTEGRATION_CONFIG = gql`
    query hasIntegrationConfig {
        hasIntegrationConfig
    }
`;
